import { useAtom } from "jotai";
import { useEffect, useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Filter } from "lucide-react";
import { companyAtom } from "@/store/company";
import { datesAtom } from "@/store/dates";
import { ApiResponse } from "@/lib/api";
import { CompanyCategory } from "@/types";
import { getApiUrl } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { categoriesAtom } from "@/store/categories";
import { levelsAtom } from "@/store/levels";
import { intensityAtom } from "@/store/intensity";
import { Input } from "@/components/ui/input";

export default function EstandardReport() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [company] = useAtom(companyAtom);
  const [dates, setDates] = useAtom(datesAtom);
  const [categories, setCategories] = useAtom(categoriesAtom);
  const [levels, setLevels] = useAtom(levelsAtom);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [intensity, setIntensity] = useAtom(intensityAtom);

  // year utilities
  const currentYear = new Date().getFullYear();
  // Generate year options (10 years before and after current year)
  const yearOptions = Array.from(
    { length: 21 },
    (_, i) => currentYear - 10 + i,
  );

  // Generate year options (10 years before onlyyear)
  const currentYearOptions: number[] = Array.from(
    { length: 11 },
    (_, i) => currentYear - 10 + i,
  );

  const { data: categoriesData } = useQuery<ApiResponse<CompanyCategory>>({
    queryKey: ["company-categories", company?.id],
    queryFn: async () => {
      const urlParams = `company=${company?.id}&is_active=true`;

      const response = await fetch(
        getApiUrl(`company-categories/?${urlParams}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      return response.json();
    },
    enabled: !!token && !!company,
  });

  // Handle year change
  const handleYearChange = (value: string) => {
    setSelectedYear(parseInt(value));
  };

  const handleFilterClick = () => {
    // refetch();
    setDates({
      fromDate: `${selectedYear}-01-01`,
      toDate: `${selectedYear}-12-31`,
    });
    window.open("/report/estandard", "_blank");
  };

  useEffect(() => {
    if (categoriesData?.results) {
      const res = categoriesData?.results?.map((cat: CompanyCategory) =>
        Number(cat.category),
      );
      console.log("res", res);
      setCategories(res);
      setLevels([1, 2]);
    }
  }, [categoriesData]);

  return (
    <div className="p-6 flex flex-col gap-4 max-w-4xl">
      <div className="text-2xl font-semibold">Estandard Report</div>
      <div className="text-md font-medium"> {company?.name}</div>
      {/* Year Selector */}
      <h2 className="text-lg font-medium">{t("dashboard.year")}</h2>
      <div className="flex items-center gap-2">
        {/* <span className="text-sm font-medium">{t("dashboard.year")}:</span> */}
        <Select
          value={selectedYear.toString()}
          onValueChange={handleYearChange}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {currentYearOptions.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Intensity Selection */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium">{t("customReport.intensity")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="number"
            min="0"
            placeholder={t("customReport.intensityValue")}
            value={intensity.value ?? ""}
            onChange={(e) =>
              setIntensity({ ...intensity, value: Number(e.target.value) })
            }
            className="p-3 border rounded-lg"
          />
          <Select
            value={intensity.unit ?? undefined}
            onValueChange={(value) =>
              setIntensity({ ...intensity, unit: value })
            }
          >
            <SelectTrigger className="p-3 border rounded-lg">
              <SelectValue placeholder={t("customReport.intensityUnit")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="worker">
                {t("customReport.workers")}
              </SelectItem>
              <SelectItem value="m2">{t("customReport.surface")}</SelectItem>
              <SelectItem value="office">
                {t("customReport.offices")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filter Button */}
      <Button
        variant="default"
        size="icon"
        // disabled={!company?.id || isLoadingCO2}
        onClick={handleFilterClick}
      >
        <Filter className="h-4 w-4" />
      </Button>
    </div>
  );
}
